import type { PropsWithChildren } from 'react'
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { UPLOAD_CONCURRENCY_LIMIT } from 'shared/constants'
import { createConcurrencyLimiter } from 'shared/utils/concurrency-limiter'
import { AttachmentGallery } from 'modules/community/components/attachment-gallery/attachment-gallery'
import type {
  AudioUploadAttachment,
  PostAttachmentType,
} from 'modules/community/types/post-attachment'
import { AttachmentUploadStatusEnum } from 'modules/community/types/post-attachment'

type AttachmentUpdate = Partial<
  Pick<AudioUploadAttachment, 'dataFileId' | 'name' | 'status' | 'url'>
>

interface PostAttachmentsContextInterface {
  attachments?: PostAttachmentType[]
  initialGallerySlide?: number
  isGalleryVisible: boolean
  isEditable: boolean
  isUploading: boolean
  limit: ReturnType<typeof createConcurrencyLimiter>
  setIsGalleryVisible: (isGalleryVisible: boolean) => void
  setInitialGallerySlide: (initialGallerySlide?: number) => void
  addAttachment: (attachment: PostAttachmentType) => void
  removeAttachment: (id: number | string) => void
  updateAttachment: (id: number | string, attachment: AttachmentUpdate) => void
  reorder: (activeId: number | string, targetId: number | string) => void
}

const PostAttachmentsContext = createContext<PostAttachmentsContextInterface>({
  attachments: [],
  isGalleryVisible: false,
  isEditable: false,
  isUploading: false,
  limit: createConcurrencyLimiter(UPLOAD_CONCURRENCY_LIMIT),
  setInitialGallerySlide: () => {},
  setIsGalleryVisible: () => {},
  addAttachment: () => {},
  removeAttachment: () => {},
  updateAttachment: () => {},
  reorder: () => {},
})

export const usePostAttachmentsContext = (): PostAttachmentsContextInterface =>
  useContext(PostAttachmentsContext)

interface PostAttachmentsProviderProps extends PropsWithChildren {
  attachments?: PostAttachmentType[]
  isEditable?: boolean
  onChange?: (attachments: PostAttachmentType[]) => void
}

export interface AttachmentsRefInterface {
  clear: () => void
}

export const PostAttachmentsProvider = forwardRef<
  AttachmentsRefInterface,
  PostAttachmentsProviderProps
>(function PostAttachmentsProvider(props, ref) {
  const [initialGallerySlide, setInitialGallerySlide] = useState<number>()
  const [isGalleryVisible, setIsGalleryVisible] = useState<boolean>(false)
  const [attachments, setAttachments] = useState<PostAttachmentType[]>(props.attachments ?? [])
  const limiterRef = useRef(createConcurrencyLimiter(UPLOAD_CONCURRENCY_LIMIT))

  const addAttachment = useCallback((attachment: PostAttachmentType) => {
    setAttachments(prev => [...prev, attachment])
  }, [])

  const isUploading = useMemo(
    () => attachments.some(({ status }) => status === AttachmentUploadStatusEnum.Uploading),
    [attachments],
  )

  const removeAttachment = useCallback((id: number | string) => {
    setAttachments(prev => prev.filter(a => a.id !== id))
  }, [])

  const updateAttachment = useCallback((id: number | string, attachment: AttachmentUpdate) => {
    setAttachments(prev => prev.map(a => (a.id === id ? { ...a, ...attachment } : a)))
  }, [])

  const reorder = useCallback((activeId: number | string, targetId: number | string) => {
    setAttachments(prev => {
      const activeIndex = prev.findIndex(a => a.id === activeId)
      const targetIndex = prev.findIndex(a => a.id === targetId)

      if (activeIndex === -1 || targetIndex === -1 || activeIndex === targetIndex) {
        return prev
      }

      const copy = [...prev]
      const element = copy.splice(activeIndex, 1)[0]
      copy.splice(targetIndex, 0, element)
      return copy
    })
  }, [])

  useImperativeHandle(ref, () => ({
    clear: () => {
      setAttachments([])
    },
  }))

  useEffect(() => {
    if (props.onChange) {
      props.onChange(attachments)
    }
  }, [attachments, props.onChange])

  return (
    <PostAttachmentsContext.Provider
      value={{
        attachments,
        isGalleryVisible,
        initialGallerySlide,
        isUploading,
        isEditable: props.isEditable ?? false,
        limit: limiterRef.current,
        setIsGalleryVisible,
        setInitialGallerySlide,
        addAttachment,
        removeAttachment,
        updateAttachment,
        reorder,
      }}
    >
      {props.children}
      {isGalleryVisible && <AttachmentGallery />}
    </PostAttachmentsContext.Provider>
  )
})
